import { useEffect, useRef, useState } from 'react';
import { ensureFreshAccessTokenForStream } from '@shared/api/ensureFreshAccessTokenForStream';
import {
  openQuotationEventStream,
  type ConnectionState,
  type QuotationEvent,
} from '../lib/quotationEventStream';

// CSC-297 — small EventTarget-based local bus that fans the single SSE stream
// out to whichever components want to listen. Lets `QuotationWorkspacePage`
// own the SSE connection while `QuotationMasterList` subscribes to events
// without prop-drilling handlers down through the layout. Module-scoped so
// every component that opens the workspace shares one bus instance.
const eventTarget = new EventTarget();
const EVENT_NAME = 'csc:quotation-event';
const RECONNECT_NAME = 'csc:quotation-reconnect';

export function onQuotationEvent(handler: (evt: QuotationEvent) => void): () => void {
  const listener = (e: Event) => handler((e as CustomEvent<QuotationEvent>).detail);
  eventTarget.addEventListener(EVENT_NAME, listener);
  return () => eventTarget.removeEventListener(EVENT_NAME, listener);
}

export function onQuotationStreamReconnect(handler: () => void): () => void {
  const listener = () => handler();
  eventTarget.addEventListener(RECONNECT_NAME, listener);
  return () => eventTarget.removeEventListener(RECONNECT_NAME, listener);
}

interface UseQuotationEventStreamResult {
  connectionState: ConnectionState;
}

// Opens a single SSE connection per call site. Intended to be called exactly
// once at the workspace page level — child components subscribe via the bus
// helpers above instead of re-opening the connection.
export function useQuotationEventStream(): UseQuotationEventStreamResult {
  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting');
  // Strict mode in dev calls effects twice. The ref guards against opening two
  // EventSources during that double-invoke.
  const handleRef = useRef<{ close: () => void } | null>(null);

  useEffect(() => {
    // E7 item 4 — visibility gating, adopting the shape useGateStateStream already uses. A hidden tab
    // held its EventSource open indefinitely: browsers allow only ~6 connections per origin, so idle
    // background tabs starved the foreground one, and each also kept re-presenting an expiring token.
    // Closing on hide and reopening on show returns the connection slot and guarantees the reopen builds
    // a fresh URL.
    //
    // Missing events while hidden is safe BECAUSE the reopen fires the consumer's catch-up path: the
    // stream is a freshness nudge, and the refetch on reconnect is what makes the data true.
    const open = () => {
      if (handleRef.current) return;
      handleRef.current = openQuotationEventStream({
        onEvent: (evt) => {
          eventTarget.dispatchEvent(
            new CustomEvent<QuotationEvent>(EVENT_NAME, { detail: evt }),
          );
        },
        onConnectionStateChange: setConnectionState,
        onReconnect: () => {
          eventTarget.dispatchEvent(new Event(RECONNECT_NAME));
        },
      });
    };

    const close = () => {
      handleRef.current?.close();
      handleRef.current = null;
    };

    let disposed = false;

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') {
        close();
        return;
      }
      // Refresh BEFORE reopening. The reopen calls connect() directly rather than going through
      // scheduleReconnect(), so it does not inherit that path's refresh — and this is exactly the
      // idle-tab case the fix exists for: a tab hidden past the 15-minute expiry would otherwise
      // reopen with a dead token, be rejected, and only recover on the next backoff tick. It
      // self-heals either way; this removes the wasted round trip and the flash of failure.
      void ensureFreshAccessTokenForStream().then(() => {
        // The await yields: the effect may have been torn down, or the tab hidden again, meanwhile.
        if (disposed || document.visibilityState !== 'visible') return;
        open();
        // Reopening is a gap, exactly like a dropped connection — tell consumers to catch up. The
        // stream's own onReconnect only fires for reconnects within one handle's life, so a
        // close/reopen would otherwise leave the page silently stale for whatever it missed.
        eventTarget.dispatchEvent(new Event(RECONNECT_NAME));
      });
    };

    close(); // Strict mode double-invoke: never leave a previous handle open.
    // First open needs no refresh: AuthInitializer already refreshes a stale token at mount.
    if (document.visibilityState === 'visible') open();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisibility);
      close();
    };
  }, []);

  return { connectionState };
}
